
import { ClipboardDocumentListIcon, InformationCircleIcon, ShoppingCartIcon } from '@heroicons/vue/24/outline'
import { computed, defineComponent, ref } from 'vue'

import RefundModal from '@/components/neue/RefundModal.vue'
import { useEntitlements } from '@/composables/useEntitlements'
import { useToast } from '@/composables/useToast'
import { canOpenTierView } from '@/config/membershipTierView'
import { serverDayToDateObject } from '@/helpers/dates'
import { awaitingInvoicePayment, collectedOutsideStripe } from '@/helpers/orderPayment'
import { maxRefundCentsFor } from '@/helpers/refund'
import privilegeService from '@/services/privilege.service'
import api from '@/store/api'
import { mapRelationship, mapRelationships } from '@/store/mappers'

export default defineComponent({
  components: {
    ClipboardDocumentListIcon,
    InformationCircleIcon,
    RefundModal,
    ShoppingCartIcon
  },
  props: {
    bottles: {
      type: Object,
      required: true
    },
    showActions: {
      type: Boolean,
      required: false,
      default: false
    },
    isLoading: {
      type: Boolean,
      required: true,
      default: false
    },
    isEmpty: {
      type: Boolean,
      required: true,
      default: false
    },
    hasError: {
      type: Boolean,
      required: true,
      default: false
    },
    emitPagination: {
      type: Boolean,
      required: false,
      default: false
    },
    pageSize: {
      type: Number,
      required: false,
      default: 25
    },
    onPageChange: {
      type: Function,
      required: false,
      default: null
    },
    totalRecords: {
      type: Number,
      required: false,
      default: null
    },
    initialPage: {
      type: Number,
      required: false,
      default: 0
    },
    emptyStateHeader: {
      type: String,
      required: false,
      default: 'No results found'
    },
    emptyStateSubheader: {
      type: String,
      required: false,
      default: "Try adjusting your search to find what you're looking for"
    },
    tab: {
      type: String,
      required: true,
      default: 'contacts'
    },
    stacked: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['on-page-change', 'on-action-executed'],
  setup(props, { emit }) {
    const local = ref({
      bottles: props.bottles.map(bottle => {
        return {
          ...bottle,
          expanded: false
        }
      }),
      page: 0,
      modal: {
        mode: '',
        type: '',
        content: {}
      },
      status: {
        isLoading: {
          refundModal: false
        }
      }
    })

    const getStoreObject = id => {
      return mapRelationship({ type: 'store', id: id })
    }

    const expandCollapseCartItemsForRow = bottle => {
      bottle.expanded = !bottle.expanded
    }

    const getCheckoutObject = id => {
      return mapRelationship({ type: 'checkout', id: id })
    }

    const getBottlePaymentObject = id => {
      return mapRelationship({ type: 'bottle_payment', id: id })
    }

    const getFulfilledDate = id => {
      const fulfillment = mapRelationship({ type: 'fulfillment_slot', id: id })
      if (!fulfillment) return null
      return new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium'
      }).format(serverDayToDateObject(fulfillment.attributes.fulfillment_date))
    }

    const cartItemsForBottle = (bottle, expanded) => {
      const cart = mapRelationship(bottle.relationships?.cart?.data)
      const items = mapRelationships(cart?.relationships?.cart_items?.data) ?? []
      return expanded ? items : items.slice(0, 3)
    }

    const getFulfillmentSlot = item => {
      return mapRelationship(item?.relationships?.fulfillment_slot?.data)
    }

    const getFulfillmentMethod = id => {
      const fulfillmentSlot = mapRelationship({ type: 'fulfillment_slot', id: id })
      const fulfillmentMethod = mapRelationship(fulfillmentSlot?.relationships?.fulfillment_method?.data)
      if (!fulfillmentMethod) return null
      return fulfillmentMethod
    }

    const getCustomerObject = id => {
      return mapRelationship({ type: 'conversation_user_pair', id: id })
    }

    const getPaidDate = id => {
      const payment = mapRelationship({ type: 'bottle_payment', id: id })
      if (!payment) return null
      return new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium'
      }).format(Date.parse(payment.attributes.created_at))
    }

    const getPaidTime = id => {
      const payment = mapRelationship({ type: 'bottle_payment', id: id })
      if (!payment) return null
      return new Intl.DateTimeFormat([], {
        hour: 'numeric',
        minute: '2-digit'
      }).format(new Date(payment.attributes.created_at))
    }

    const getMembershipTier = id => {
      return mapRelationship({ type: 'membership_tier', id: id })
    }

    const getDistributionList = id => {
      return mapRelationship({ type: 'distribution_list', id: id })
    }

    const getBottleCycle = id => {
      return mapRelationship({ type: 'bottle_cycle', id: id })
    }

    const getBottleCancellationStatus = bottle => {
      return bottle?.attributes?.cancellation_status
    }

    const canReadOrders = computed(() => privilegeService.canAccessResourceAction('orders', null, 'read'))
    const canUpdateOrders = computed(() => privilegeService.canAccessResourceAction('orders', null, 'update'))
    const canReadStores = computed(() => privilegeService.canAccessResourceAction('sell', null, 'read'))
    const canReadLists = computed(() => privilegeService.canAccessResourceAction('customers', null, 'read'))
    const canReadFulfillmentMethods = computed(() => privilegeService.canAccessResourceAction('sell', null, 'read'))
    const canReadCustomerProfile = computed(() =>
      privilegeService.canAccessResourceAction('customer_profile', null, 'read')
    )
    const { user } = useEntitlements()
    const canReadMembershipTiers = computed(() => canOpenTierView(user.value))
    const canReadDrops = computed(() => privilegeService.canAccessResourceAction('sell', null, 'read'))

    const getItemActionsMenu = attributes => {
      let actionsMenu = {
        label: 'Actions',
        items: []
      }
      if (attributes.link && canReadOrders.value)
        actionsMenu.items.push({
          name: 'View order',
          handler: 'handleViewOrder'
        })
      if (attributes.actions.refund && canUpdateOrders.value)
        actionsMenu.items.push({
          name: 'Refund order',
          handler: 'handleRefund'
        })
      if (attributes.actions.mark_as_paid && canUpdateOrders.value)
        actionsMenu.items.push({
          name: 'Mark as paid',
          handler: 'handleMarkAsPaid'
        })
      if (attributes.actions.mark_as_unpaid && canUpdateOrders.value)
        actionsMenu.items.push({
          name: 'Mark as unpaid',
          handler: 'handleMarkAsUnpaid'
        })
      if (attributes.actions.cancel_order && canUpdateOrders.value)
        actionsMenu.items.push({
          name: 'Cancel order',
          handler: 'handlerCancelOrder'
        })
      if (attributes.actions.un_cancel_order && canUpdateOrders.value)
        actionsMenu.items.push({
          name: 'Reactivate order',
          handler: 'handlerReactivateOrder'
        })
      return actionsMenu
    }

    const { showToast } = useToast()

    // BOT-1764: unmarking an invoice order reissues the invoice and leaves the
    // order invoiced, so take the status from the reply and load its payment.
    const markPayment = (id, action, status) =>
      api
        .call('POST', `bottles/${id}/${action}?included=bottle_invoice,bottle_payment`)
        .then(response => {
          const bottle = props.bottles.find(x => parseFloat(x.id) === id)
          if (bottle) bottle.attributes.status = response?.data?.data?.attributes?.status ?? status
        })
        .catch(() => showToast("Couldn't update the payment. Please try again.", { type: 'error' }))

    const handleMarkAsPaid = id => markPayment(id, 'pay', 'paid')

    const handleMarkAsUnpaid = id => markPayment(id, 'unpay', 'pending')

    // POST: cancel order
    const handlerCancelOrder = id => {
      api
        .call('PUT', `bottles/${id}`, {
          bottle: {
            cancellation_status: 'cancelled'
          }
        })
        .then(() => {
          emit('on-action-executed')
        })
    }

    // POST: reactivate order
    const handlerReactivateOrder = id => {
      api
        .call('PUT', `bottles/${id}`, {
          bottle: {
            cancellation_status: 'active'
          }
        })
        .then(() => {
          emit('on-action-executed')
        })
    }

    // POST: apply refund
    const saveRefundModal = refundAmountCents => {
      const id = local.value.modal.content.bottleId
      local.value.status.isLoading.refundModal = true

      api
        .call('POST', `bottles/${id}/refunds`, {
          refund: {
            amount: refundAmountCents
          }
        })
        .then(() => {
          local.value.status.isLoading.refundModal = false
          resetModalContent()
        })
        .catch(error => {
          local.value.status.isLoading.refundModal = false
          local.value.modal.content.onSubmitMessage = 'Something went wrong!'
          local.value.modal.content.onSubmitLog = error
        })
    }

    const handleRefund = id => {
      const item = props.bottles.find(x => parseFloat(x.id) === parseFloat(id))
      const payment = getBottlePaymentObject(item?.relationships?.bottle_payment?.data?.id)
      const checkout = getCheckoutObject(item?.relationships?.checkout?.data?.id)
      const maxRefundCents = maxRefundCentsFor(payment, checkout)
      local.value.modal.type = 'refundModal'
      local.value.modal.mode = 'add'
      local.value.modal.content = {
        bottleId: id,
        maxRefundCents
      }
    }

    const refundAmount = item => {
      if (item.attributes.refund_status !== 'not_refunded') {
        return ` - ${getCheckoutObject(item.relationships.checkout?.data?.id)?.attributes?.refunded_amount?.format}`
      }
    }

    const handleViewOrder = id => {
      const bottleLink = props.bottles.find(x => parseFloat(x.id) === id).attributes.link ?? null
      if (bottleLink) window.open(bottleLink, '_blank')
      return
    }

    const handleActionsMenuClick = (action, id) => {
      switch (action) {
        case 'handleMarkAsPaid':
          return handleMarkAsPaid(id)
        case 'handleMarkAsUnpaid':
          return handleMarkAsUnpaid(id)
        case 'handleRefund':
          return handleRefund(id)
        case 'handleViewOrder':
          return handleViewOrder(id)
        case 'handlerCancelOrder':
          return handlerCancelOrder(id)
        case 'handlerReactivateOrder':
          return handlerReactivateOrder(id)
      }
    }

    const handlePageChange = p => {
      if (props.emitPagination) emit('on-page-change', p)
      else local.value.page = p
    }

    const resetModalContent = () => {
      local.value.modal.type = ''
      local.value.modal.mode = ''
      local.value.modal.content = {}
    }

    const paymentStatus = (paymentStatus, bottlePaymentId) => {
      // return paymentStatus, bottlePaymentId
      if (bottlePaymentId) {
        const bottlePayment = getBottlePaymentObject(bottlePaymentId)
        const orderMarkedAsPaid =
          bottlePayment?.attributes?.reason == 'mark_as_paid' || bottlePayment?.attributes?.reason == 'less_than_50c'
        if (orderMarkedAsPaid && paymentStatus == 'paid')
          return {
            text: 'Marked Paid',
            classTheme: 'success'
          }
      }
      if (paymentStatus == 'paid')
        return {
          text: 'Paid',
          classTheme: 'success'
        }
      else if (paymentStatus === 'invoiced')
        // BOT-1680: placed on an invoice that is still open.
        return {
          text: 'Invoiced',
          classTheme: 'warning'
        }
      else if (paymentStatus == 'expired')
        return {
          text: 'Expired',
          classTheme: 'error'
        }
      else if (paymentStatus == 'benign' || paymentStatus == 'skipped')
        return {
          text: paymentStatus.replace('-', ' '),
          classTheme: 'default'
        }
      else
        return {
          text: paymentStatus.replace('-', ' '),
          classTheme: 'warning'
        }
    }

    return {
      local,
      getStoreObject,
      getCheckoutObject,
      getBottlePaymentObject,
      getFulfilledDate,
      getFulfillmentMethod,
      getFulfillmentSlot,
      getCustomerObject,
      getPaidDate,
      refundAmount,
      getPaidTime,
      getItemActionsMenu,
      getMembershipTier,
      getDistributionList,
      getBottleCycle,
      handleActionsMenuClick,
      saveRefundModal,
      resetModalContent,
      handlePageChange,
      paymentStatus,
      awaitingInvoicePayment,
      collectedOutsideStripe,
      handlerCancelOrder,
      handlerReactivateOrder,
      getBottleCancellationStatus,
      cartItemsForBottle,
      expandCollapseCartItemsForRow,
      mapRelationship,
      serverDayToDateObject,
      canReadOrders,
      canReadStores,
      canReadLists,
      canReadFulfillmentMethods,
      canReadCustomerProfile,
      canReadMembershipTiers,
      canReadDrops
    }
  }
})
